import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lightbulb, Database, CheckCircle, Rocket, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const Process = () => {
  const [activeStep, setActiveStep] = useState(null);
  
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Our Process", url: "/process" }
  ];

  // Function to scroll to the associated card when icon is clicked
  const scrollToCard = (index) => {
    const cardElement = document.getElementById(`process-card-${index}`);
    if (cardElement) {
      const headerOffset = 100; // Account for fixed header + some padding
      const elementPosition = cardElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI Model Lifecycle Process",
    "description": "From data to deployment—a 4-step process built for reliable AI models through planning, preparation, evaluation, and deployment.",
    "provider": {
      "@type": "Organization",
      "name": "Orboroi"
    }
  };

  const processSteps = [
    {
      icon: Lightbulb,
      title: 'Plan & Strategize',
      number: '01',
      description: 'We begin by understanding your model architecture, data requirements, and production goals to create a tailored annotation and evaluation strategy.',
      details: [
        'Align annotation guidelines with model objectives',
        'Define quality metrics and acceptance criteria',
        'Establish data pipeline architecture',
        'Create custom workflows for your use case'
      ],
      contribution: 'Sets the foundation for data certainty by ensuring every annotation decision supports your model\'s production requirements.',
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/n3nwsse5_Plan%20%26%20Strategize.png'
    },
    {
      icon: Database,
      title: 'Data Operations',
      number: '02',
      description: 'Our specialized teams execute high-fidelity annotation, data cleaning, and validation with rigorous quality controls at every step.',
      details: [
        'Production-grade annotation by domain experts',
        'Multi-layer quality assurance processes',
        'Real-time quality monitoring and feedback',
        'Continuous guideline refinement'
      ],
      contribution: 'Delivers training data with the precision and consistency required for reliable model performance in production environments.',
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/8yzw08zx_Data%20Operations.png'
    },
    {
      icon: CheckCircle,
      title: 'Model Evaluation',
      number: '03',
      description: 'Comprehensive testing and validation to identify edge cases, measure performance, and ensure your model is production-ready.',
      details: [
        'Multi-layer QA and error detection',
        'Edge case identification and management',
        'Performance benchmarking against standards',
        'Human-in-the-loop validation'
      ],
      contribution: 'Provides confidence that your model will perform reliably when deployed, reducing risk and preventing costly failures.',
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/5xqt8em7_Model%20Evaluation.png'
    },
    {
      icon: Rocket,
      title: 'Delivery & Scale',
      number: '04',
      description: 'Seamless integration with your systems and scalable workforce management to support ongoing model improvement and production needs.',
      details: [
        'Smooth handoff and system integration',
        'Workforce scaling from pilot to production',
        'Continuous monitoring and improvement',
        'Ongoing support and optimization'
      ],
      contribution: 'Ensures your AI system can scale reliably with maintained quality as your production needs grow.',
      image: 'https://images.unsplash.com/photo-1732209556962-df3c1334bc47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHx0ZWNoJTIwdGVhbSUyMGNvbGxhYm9yYXRpb24lMjByZW1vdGUlMjB3b3JrJTIwQUklMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NzQyOTg1OTZ8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="Our Process - From Data to Deployment"
        description="A 4-step AI model lifecycle process: plan, prepare, evaluate, and deploy. Built for reliable AI models with data certainty at every stage."
        canonical="/process"
        type="website"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              From Data to Deployment—<span className="text-[#379F7F]">A Process Built for Reliable AI Model</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A proven four-step methodology that ensures data certainty and production-ready 
              AI models from planning through deployment and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Process Flow */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          {/* Animated Timeline */}
          <div className="hidden lg:flex justify-between items-center mb-20 relative">
            {/* Base line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-700 -translate-y-1/2"></div>
            
            {/* Animated progress line */}
            <div 
              className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-[#379F7F] to-[#2d7d66] -translate-y-1/2 transition-all duration-1000 ease-out"
              style={{ 
                width: activeStep !== null ? `${((activeStep + 1) / processSteps.length) * 100}%` : '0%' 
              }}
            ></div>
            
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="relative z-10 flex flex-col items-center cursor-pointer group"
                onMouseEnter={() => setActiveStep(index)}
                onMouseLeave={() => setActiveStep(null)}
                onClick={() => {
                  setActiveStep(activeStep === index ? null : index);
                  scrollToCard(index);
                }}
              >
                {/* Pulse ring effect */}
                {activeStep === index && (
                  <div className="absolute top-0 w-20 h-20 rounded-full bg-[#379F7F] opacity-30 animate-ping"></div>
                )}
                
                {/* Glow effect */}
                <div className={`absolute top-0 w-16 h-16 rounded-full transition-all duration-500 ${
                  activeStep === index 
                    ? 'bg-[#379F7F] opacity-40 blur-xl scale-150' 
                    : 'opacity-0'
                }`}></div>
                
                {/* Main icon circle */}
                <div className={`relative w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all duration-500 transform ${
                  activeStep === index 
                    ? 'bg-[#379F7F] text-black scale-125 shadow-lg shadow-[#379F7F]/50 rotate-[360deg]' 
                    : 'bg-gray-800 text-white group-hover:bg-[#379F7F] group-hover:text-black group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#379F7F]/30'
                }`}>
                  <step.icon className={`transition-all duration-300 ${
                    activeStep === index ? 'w-9 h-9' : 'w-8 h-8'
                  }`} />
                </div>
                
                {/* Number label with animation */}
                <span className={`text-sm font-semibold transition-all duration-300 transform ${
                  activeStep === index 
                    ? 'text-[#379F7F] scale-110 font-bold' 
                    : 'text-gray-400 group-hover:text-[#379F7F] group-hover:scale-105'
                }`}>
                  {step.number}
                </span>
                
                {/* Step title on hover */}
                <div className={`absolute top-24 left-1/2 -translate-x-1/2 whitespace-nowrap transition-all duration-300 ${
                  activeStep === index 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-2 pointer-events-none'
                }`}>
                  <span className="text-xs text-[#379F7F] font-semibold bg-gray-900 px-3 py-1 rounded-full border border-[#379F7F]/30">
                    {step.title}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Process Cards */}
          <div className="space-y-12">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                id={`process-card-${index}`}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center scroll-mt-24`}
              >
                {/* Image */}
                <div className="lg:w-1/2">
                  <div className="relative rounded-lg overflow-hidden group">
                    <img 
                      src={step.image}
                      alt={step.title}
                      className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute top-6 left-6">
                      <div className="text-6xl font-bold text-[#379F7F] opacity-50">
                        {step.number}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <Card className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <step.icon className="w-12 h-12 text-[#379F7F]" />
                        <span className="text-4xl font-bold text-gray-700">{step.number}</span>
                      </div>
                      <CardTitle className="text-white text-3xl mb-2">{step.title}</CardTitle>
                      <CardDescription className="text-gray-300 text-base">
                        {step.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-6">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-300">
                            <ArrowRight className="w-5 h-5 text-[#379F7F] mt-0.5 flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="bg-gray-900 p-4 rounded-lg border-l-4 border-[#379F7F]">
                        <p className="text-sm font-semibold text-[#379F7F] mb-1">Data Certainty Impact</p>
                        <p className="text-gray-300 text-sm">{step.contribution}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Process Works */}
      <section className="py-12 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
              Why Our <span className="text-[#379F7F]">Process</span> Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">End-to-End Expertise</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    We understand every stage of the AI lifecycle, from training data to production deployment, 
                    ensuring seamless transitions and consistent quality.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Human-in-the-Loop</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    Our processes integrate human expertise at critical points, catching edge cases and 
                    ensuring your models handle real-world complexity.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Proven Quality Standards</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    With a 99% first-batch acceptance rate, our multi-layer QA processes deliver 
                    consistent, production-ready results.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Scalable Operations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    From pilot projects to enterprise-scale deployments, our workforce management 
                    and operational expertise scale with your needs.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your <span className="text-[#379F7F]">Lifecycle Journey</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how our proven process can help you achieve data certainty 
              and deploy production-ready AI models faster.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg px-8 py-6"
            >
              <Link to="/contact">Schedule a Consultation</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Process;
